import { Box, Flex, HStack, Image, Input, InputGroup, Stack, Text, VStack } from "@chakra-ui/react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { deleteAllSearchLogs, deleteSearchLog, getMeilisearchAutoComplete, getRecentSearchLogs, getSearchHotKeywords } from "../../api";
import LogInOnly from "../../components/Auth/LogInOnly";
import Footer from "../../components/Layout/Footer";
import LargeTemplate from "../../components/Layout/LargeTemplate";
import { colors } from "../../styles";
import { IGetSearchHotKeywordsResponse, IMeilisearchAutoComplete, ISearchLog } from "../../types";
import MyIcon from "../../components/Icon/MyIcon";

export default function Home() {

    /* navigate */

    const navigate = useNavigate();

    /* search bar component */

    function SearchBar() {

        ////////////////////////////// query

        const [query, setQuery] = useState("");

        ////////////////////////////// auto complete

        const [isSearchInputFocused, setIsSearchInputFocused] = useState<boolean>(false);

        const searchBoxRef = useRef<HTMLDivElement | null>(null);
        const inputRef = useRef<HTMLInputElement | null>(null);

        ////////////////////////////// input 디바운스

        const [debouncedQuery, setDebouncedQuery] = useState<string>("");

        useEffect(() => {

            const handler = setTimeout(() => {
                setDebouncedQuery(query);
            }, 300);

            return () => {
                clearTimeout(handler);
            };

        }, [query]);

        ////////////////////////////// meilisearch 자동 완성

        const trimmedDebouncedQuery = debouncedQuery.trim();

        const { data: meilisearchAutoCompleteData } = useQuery<IMeilisearchAutoComplete>({
            queryKey: ['getMeilisearchAutoCompleteHome', trimmedDebouncedQuery],
            queryFn: () => getMeilisearchAutoComplete({ searchKeyword: trimmedDebouncedQuery }),
            enabled: trimmedDebouncedQuery.length > 1
        });

        type MeilisearchAutoCompleteItem = {
            type: string;
            value: string;
        }

        const autoCompleteArray: MeilisearchAutoCompleteItem[] = [
            ...(meilisearchAutoCompleteData?.products ?? []).map((item) => {return {type: item.type, value: item.value}}),
            ...(meilisearchAutoCompleteData?.keywords ?? []).map((item) => {return {type: item.type, value: item.value}}),
        ];

        const filteredAutoCompleteArray = autoCompleteArray.filter((item) =>
            item.value.toLowerCase().includes(query.toLowerCase())
        );

        const shouldShowAutoComplete =
            isSearchInputFocused &&
            query.trim().length > 1 &&
            filteredAutoCompleteArray.length > 0;

        ////////////////////////////// handleKeyDown (Enter 검색)

        const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {

            // 만약 눌린 키가 "Enter"가 아니면 아무 동작도 하지 않고 함수 종료
            if (event.key !== "Enter") return;

            // IME(입력기) 조합 중에 Enter가 눌린 경우(예: 한글 변환 중 Enter)는 무시하고 함수 종료
            if ((event.nativeEvent as any).isComposing) return;
            
            // 현재 입력 필드의 값을 가져와 양 끝 공백을 제거한 후, keyword 변수에 저장
            const keyword = event.currentTarget.value.trim();

            // 만약 keyword가 빈 문자열(입력값이 없음)이면 함수 종료
            if (!keyword) return;

            // 검색 결과 페이지로 이동, 쿼리스트링으로 검색어를 전달
            navigate(`/search?q=${encodeURIComponent(keyword)}`);
        };

        ////////////////////////////// handleSelectAutoComplete (자동완성 선택 시 검색)

        const handleSelectAutoComplete = (keyword: string) => {

            const trimmed = keyword.trim();
            if (!trimmed) return;

            setQuery(trimmed);
            navigate(`/search?q=${encodeURIComponent(trimmed)}`);

            setIsSearchInputFocused(false);
            inputRef.current?.blur();
        };

        ////////////////////////////// auto complete: 바깥 클릭 시 닫기

        useEffect(() => {

            const handleClickOutside = (event: MouseEvent) => {

                if (!searchBoxRef.current) return;

                if (!searchBoxRef.current.contains(event.target as Node)) {

                    setIsSearchInputFocused(false);
                    inputRef.current?.blur();
                }
            };

            if (isSearchInputFocused) {
                document.addEventListener("mousedown", handleClickOutside);
            }

            return () => {
                document.removeEventListener("mousedown", handleClickOutside);
            };

        }, [isSearchInputFocused]);

        return (
            <Box
                ref={searchBoxRef}
                width={"100%"}
                display="flex"
                justifyContent="center"
                position="relative"
                zIndex={10}
            >
                <Box
                    width={{ 
                        base: "90%", 
                        sm: "90%", 
                        md: "75%" ,
                        lg: "60%",
                        xl: "60%"
                    }}
                >
                    <InputGroup
                        margin="0 auto"
                        padding="0"
                        backgroundColor={colors.blue}
                        borderRadius={24}
                        boxShadow="md"
                    >
                        <Input
                            ref={inputRef}
                            color={colors.white}
                            placeholder=" 💊  약품에 대해 단어로 검색해 보세요!"
                            _placeholder={{ fontSize: 16, color: colors.white, opacity: 0.7 }}
                            backgroundColor="transparent"
                            border="none"
                            borderRadius={24}
                            fontSize={18}
                            onChange={(event: React.ChangeEvent<HTMLInputElement>) => setQuery(event.target.value)}
                            value={query}
                            onKeyDown={handleKeyDown}
                            _focus={{
                                boxShadow: "none",
                                border: "none",
                            }}
                            height={14}
                            autoFocus
                            paddingX={5}
                            onFocus={() => setIsSearchInputFocused(true)}
                        />
                    </InputGroup>

                    {shouldShowAutoComplete && (
                        <VStack
                            alignItems={"flex-start"}
                            spacing={0}
                            position="absolute"
                            top="100%"
                            left="50%"
                            transform="translateX(-50%)"
                            width={{ 
                                base: "90%", 
                                sm: "90%", 
                                md: "75%" ,
                                lg: "60%",
                                xl: "60%"
                            }}
                            marginTop={3}
                            backgroundColor={"white"}
                            borderRadius={16}
                            boxShadow="md"
                            overflow="hidden"
                        >
                            {filteredAutoCompleteArray.map((item, idx) => (
                                <HStack
                                    key={`${item}-${idx}`}
                                    as="button"
                                    type="button"
                                    width="100%"
                                    paddingX={4}
                                    paddingY={3}
                                    spacing={2}
                                    justifyContent={"flex-start"}
                                    _hover={{ backgroundColor: colors.light }}
                                    onClick={() => handleSelectAutoComplete(item.value)}
                                >
                                    {item.type === "product" && <MyIcon src="/svg/capsule-filled.svg" alt="medicine" width={20} height={20} />}
                                    {item.type === "keyword" && <MyIcon src="/svg/search.svg" alt="search" width={20} height={20} />}
                                    <Text fontSize={16} color={colors.black}>
                                        {item.value}
                                    </Text>
                                </HStack>
                            ))}
                        </VStack>
                    )}
                </Box>
            </Box>
        );
    }

    const queryClient = useQueryClient();

    /* search hot keywords */

    const { data: searchHotKeywordsData } = useQuery<IGetSearchHotKeywordsResponse>({
        queryKey: ['getSearchHotKeywords'],
        queryFn: getSearchHotKeywords,
    });

    /* recent search logs */

    const { data: recentSearchLogs } = useQuery<ISearchLog[]>({
        queryKey: ['getRecentSearchLogs'],
        queryFn: getRecentSearchLogs,
    });

    const deleteSearchLogMutation = useMutation({
        mutationFn: deleteSearchLog,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['getRecentSearchLogs'] });
        },
    });

    const deleteAllSearchLogsMutation = useMutation({
        mutationFn: deleteAllSearchLogs,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['getRecentSearchLogs'] });
        },
    });

    /* onKeywordBtnClick */

    const onKeywordBtnClick = (keyword: string) => {
        navigate(`/search?q=${encodeURIComponent(keyword)}`);
    };

    /* render */

    return (
        <LogInOnly>
            <Flex direction={"column"} flex={"1 0 auto"} width={"100%"}>
                <LargeTemplate>

                {/* 로고 */}

                <HStack justifyContent={"center"} alignItems={"center"} marginBottom={-5}>
                    <Image src="/png/logo.png" alt="Logo" width={320} height={150} objectFit={"contain"} />
                </HStack>

                {/* 검색창 */}

                <HStack 
                    justifyContent={"center"} 
                    alignItems={"center"} 
                    py={10} 
                    marginBottom={-10}
                >
                    <SearchBar />
                </HStack>

                {/* 자주 찾는 검색어 */}

                <Stack
                    justifyContent={"center"} 
                    alignItems={"center"} 
                    width={"100%"}
                    marginTop={2}
                >
                    <HStack 
                        justifyContent={"flex-start"} 
                        alignItems={"center"} 
                        width={{ 
                            base: "90%", 
                            sm: "90%", 
                            md: "75%" ,
                            lg: "60%",
                            xl: "60%"
                        }} 
                        flexWrap={"wrap"}
                    >
                        <Text fontSize={14} fontWeight={400} color={colors.g3}>자주 찾는 검색어 : {searchHotKeywordsData?.hotKeywords.map((keyword) => keyword.keyword).join(", ") || ""}</Text>
                    </HStack>
                </Stack>

                {/* 최근 검색어 */}

                <Stack
                    justifyContent={"center"}
                    alignItems={"center"}
                    width={"100%"}
                    marginTop={4}
                >
                    <HStack
                        justifyContent={"flex-start"}
                        alignItems={"center"}
                        width={{
                            base: "90%",
                            sm: "90%",
                            md: "75%",
                            lg: "60%",
                            xl: "60%"
                        }}
                        flexWrap={"wrap"}
                    >
                        {recentSearchLogs?.map((searchLog, index) => (
                            <HStack
                                key={searchLog.id}
                                height={"48px"}
                                backgroundColor={colors.light}
                                borderRadius={"24px"}
                                justifyContent={"space-between"}
                                alignItems={"center"}
                                paddingX={5}
                                paddingY={5}
                                style={{
                                    opacity: 0,
                                    transform: "translateY(16px)",
                                    animation: `fadeInUp 0.4s cubic-bezier(0.4,0,0.2,1) ${index * 0.12 + 0.2}s forwards`
                                }}
                            >
                                <Text as={'button'} onClick={() => onKeywordBtnClick(searchLog.keyword)} fontSize={16} fontWeight={500} color={colors.g1}>
                                    {searchLog.keyword}
                                </Text>
                                <Box as='button' onClick={() => deleteSearchLogMutation.mutate({ searchLogId: searchLog.id })}>
                                    <Text fontSize={14} fontWeight={500} color={colors.g1}>✕</Text>
                                </Box>
                            </HStack>
                        ))}
                        {(recentSearchLogs?.length ?? 0) > 0 && (
                            <HStack
                                as={"button"}
                                type={"button"}
                                height={"48px"}
                                backgroundColor={colors.g5}
                                borderColor={colors.black}
                                borderRadius={"24px"}
                                justifyContent={"center"}
                                alignItems={"center"}
                                paddingX={5}
                                paddingY={5}
                                onClick={() => deleteAllSearchLogsMutation.mutate()}
                                style={{
                                    opacity: 0,
                                    transform: "translateY(16px)",
                                    animation: `fadeInUp 0.4s cubic-bezier(0.4,0,0.2,1) ${(recentSearchLogs?.length ?? 0) * 0.12 + 0.2}s forwards`
                                }}
                            >
                                <Text fontSize={16} fontWeight={500} color={colors.black}>모두 삭제</Text>
                            </HStack>
                        )}
                    </HStack>
                    {/* Animation keyframes */}
                    <style>
                        {`
                            @keyframes fadeInUp {
                                from {
                                    opacity: 0;
                                    transform: translateY(16px);
                                }
                                to {
                                    opacity: 1;
                                    transform: translateY(0px);
                                }
                            }
                        `}
                    </style>
                </Stack>

                </LargeTemplate>
                <Footer />
            </Flex>
        </LogInOnly>
    );
}
